import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getUsers, createUser, updateUser, deleteUser, type AppUser,
  getEmployeeCalendarFeeds, createEmployeeCalendarFeed, deleteEmployeeCalendarFeed,
} from '../api';
import { FeedLinkControl } from './CalendarFeedSection';

interface Props {
  onClose: () => void;
}

export default function UsersModal({ onClose }: Props) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  // Personligt kalender-link: kun de opgaver medarbejderen er sat på. Sendes til medarbejderen,
  // der tilføjer det i sin egen kalender (medarbejdere har intet login endnu).
  const [feeds, setFeeds] = useState<Record<number, string | null>>({});
  const [feedOpenId, setFeedOpenId] = useState<number | null>(null);
  const newInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    const [list, tokens] = await Promise.all([getUsers(), getEmployeeCalendarFeeds().catch(() => ({}))]);
    setUsers(list);
    setFeeds(tokens);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    await createUser(newName.trim());
    setNewName('');
    await load();
    newInputRef.current?.focus();
  };

  const handleEdit = (u: AppUser) => {
    setEditId(u.id);
    setEditName(u.name);
    setDeleteId(null);
  };

  const handleEditSave = async () => {
    if (!editName.trim() || editId === null) return;
    await updateUser(editId, editName.trim());
    setEditId(null);
    await load();
  };

  const handleDelete = async (id: number) => {
    await deleteUser(id);
    setDeleteId(null);
    await load();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="modal-title">{t('users.title')}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="p-6 space-y-1">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-4">{t('vehicles.loading')}</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">{t('users.none')}</p>
          ) : (
            <ul className="divide-y">
              {users.map(u => (
                <li key={u.id} className="py-2">
                  {deleteId === u.id ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-[var(--color-error)]">{t('users.confirm_delete', { name: u.name })}</span>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => setDeleteId(null)} className="btn btn-secondary">{t('modal.cancel')}</button>
                        <button onClick={() => handleDelete(u.id)} className="btn btn-danger">{t('users.delete')}</button>
                      </div>
                    </div>
                  ) : editId === u.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        className="input flex-1"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') setEditId(null); }}
                        autoFocus
                      />
                      <button onClick={() => setEditId(null)} className="btn btn-secondary">{t('modal.cancel')}</button>
                      <button onClick={handleEditSave} className="btn btn-primary">{t('modal.save')}</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-800">{u.name}</span>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => setFeedOpenId(feedOpenId === u.id ? null : u.id)}
                          className={`btn-icon btn-icon-primary${feeds[u.id] ? ' text-sage-700' : ''}`}
                          title={t('users.calendar_feed')}>📅</button>
                        <button onClick={() => handleEdit(u)} className="btn-icon btn-icon-primary" title={t('vehicles.edit')}>✎</button>
                        <button onClick={() => { setDeleteId(u.id); setEditId(null); }} className="btn-icon btn-icon-danger" title={t('users.delete')}>✕</button>
                      </div>
                    </div>
                  )}
                  {feedOpenId === u.id && editId !== u.id && deleteId !== u.id && (
                    <div className="mt-2 p-3 rounded-lg bg-cream-50 space-y-2">
                      <p className="text-xs text-gray-500">{t('users.calendar_feed_help', { name: u.name })}</p>
                      <FeedLinkControl
                        token={feeds[u.id] ?? null}
                        onCreate={async () => { const r = await createEmployeeCalendarFeed(u.id); setFeeds(f => ({ ...f, [u.id]: r.token })); }}
                        onDelete={async () => { await deleteEmployeeCalendarFeed(u.id); setFeeds(f => ({ ...f, [u.id]: null })); }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Tilføj ny */}
          <div className="flex items-center gap-2 pt-4 border-t mt-2">
            <input
              ref={newInputRef}
              className="input flex-1"
              placeholder={t('users.name_placeholder')}
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            />
            <button onClick={handleAdd} className="btn btn-primary shrink-0">{t('users.add')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
